import axios, { AxiosRequestConfig, CancelToken, Method, ResponseType } from "axios";
import { StringDictionary } from "../types/string-dictionary";
import UrlUtils from "./url-utils";
import generateGuid from "./generate-guid";

const ApiHeaderKey = {
    correlationId: "x-correlation-id",
    requestId: "x-request-id"
}

type RequestHelperConfig = AxiosRequestConfig & {
    headers: StringDictionary,
    method: Method,
    signal: AbortSignal | null
}

// axios.interceptors.request.use((config) => {
//     log(config);
//     return config;
// });

// axios.interceptors.response.use((response) => {
//     log(response);
//     return response;
// });

export class RequestHelper {
    baseUrl = "";

    config: RequestHelperConfig = {
        method: "GET",
        url: "",
        params: {},
        headers: {},
        data: null,
        responseType: "json",
        cancelToken: undefined,
        signal: null
    };

    callback: any | null | undefined = null;

    usePromise = false;

    errorResp: any | null | undefined = null;

    constructor(baseUrl: string, signal: AbortSignal | null = null) {
        this.baseUrl = baseUrl;
        this.config.signal = signal;
    }

    withPath(path: string | undefined) {
        if (this.baseUrl === null) {
            throw new Error("Base url not defined");
        }

        const url = UrlUtils.join(this.baseUrl, path);

        return this.withUrl(url);
    }

    withData(data: any) {
        this.config.data = data;
        return this;
    }

    withMethod(method: Method) {
        this.config.method = method;
        return this;
    }

    withUrl(url: string) {
        this.config.url = url;
        return this;
    }

    withParams(params: any) {
        this.config.params = params;
        return this;
    }

    withResponseType(responseType?: ResponseType | undefined) {
        this.config.responseType = responseType;
        return this;
    }

    withCancellationToken(cancelToken: CancelToken | undefined) {
        this.config.cancelToken = cancelToken;
        return this;
    }

    withBearerToken(token: string) {
        return this.withHeader("Authorization", `Bearer ${token}`);
    }

    usingJson() {
        return this.withHeader("Content-Type", "application/json");
    }

    withHeader(key: string, value: any) {
        if (this.config.headers == null) {
            this.config.headers = {};
        }

        this.config.headers[key] = value;
        return this;
    }

    withHeaders(headers: StringDictionary) {
        this.config.headers = headers;
        return this;
    }

    usingCallback(callback: any) {
        this.callback = callback;
        return this;
    }

    usingErrorHandling(errorResp: any) {
        this.errorResp = errorResp;
        return this;
    }

    usingPromise(): RequestHelper {
        this.usePromise = true;
        return this;
    }

    private _getConfig(excludeTrackingHeaders = false) {
        if (!excludeTrackingHeaders) {
            if (this.config.headers == null) {
                this.config.headers = {};
            }

            this.config.headers[ApiHeaderKey.correlationId] = generateGuid();
            this.config.headers[ApiHeaderKey.requestId] = generateGuid();
        }

        return this.config;
    }

    private _execute(excludeTrackingHeaders = false): any {
        this._getConfig(excludeTrackingHeaders);

        if (this.usePromise) {
            return axios(this.config);
        }

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        function handleError(method: Method, error: any) {
            if (axios.isCancel(error)) {
                console.log(method + " function request has been manually cancelled.");
            } else if (error.response) {
                // Request made and server responded
                console.log(error.response);
            } else if (error.request) {
                // The request was made but no response was received
                console.log(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log("Error", error);
            }

            if (self.errorResp === undefined || self.errorResp == null) {
                return;
            }

            if (error.response) {
                self.errorResp(error.response);
            } else if (error.request) {
                self.errorResp("Unable to connect. Network error");
            }
        }

        axios(this.config).then((response) => {
            if (self.callback) {
                self.callback(response);
            }
        }).catch((error) => {
            handleError(this.config.method, error);
        });
    }

    get<T = any>(excludeTrackingHeaders = false) {
        this._getConfig(excludeTrackingHeaders);
        return axios.get<T>(this.config.url ?? "", this.config);
    }

    formPost(data: any | null = null, excludeTrackingHeaders = false) {
        this.withHeader("Content-Type", "application/x-www-form-urlencoded");

        if (data != null) {
            this.withData(data);
        }

        return this.withMethod("POST")._execute(excludeTrackingHeaders);
    }

    post<T = any>(data: any | null = null, excludeTrackingHeaders = false) {
        this._getConfig(excludeTrackingHeaders);
        return axios.post<T>(this.config.url ?? "", data, this.config);
    }

    put<T = any>(data: any | null = null, excludeTrackingHeaders = false) {
        this._getConfig(excludeTrackingHeaders);
        return axios.put<T>(this.config.url ?? "", data, this.config);
    }

    delete<T = any>(data: any | null = null, excludeTrackingHeaders = false) {
        this._getConfig(excludeTrackingHeaders);

        if (data) {
            this.config.data = data;
        }

        return axios.delete<T>(this.config.url ?? "", this.config);
    }
}

export default RequestHelper;